"use client"

import * as React from 'react'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { updateCrud, deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { COMPLIANCE_DOCUMENT_ENTITY_ID } from '../../../lib/complianceDocument'
import {
  buildComplianceDocumentUpdatePayload,
  createComplianceDocumentFormFields,
  createComplianceDocumentFormGroups,
  createComplianceDocumentFormSchema,
  mapComplianceDocumentRowToFormValues,
  type ComplianceDocumentFormValues,
} from '../../../components/formConfig'

type DocumentRow = Record<string, unknown>

type ListResult = {
  items?: DocumentRow[]
  total?: number
}

const statusBadgeClass: Record<string, string> = {
  VALID: 'bg-green-100 text-green-800',
  EXPIRING_SOON: 'bg-yellow-100 text-yellow-800',
  EXPIRED: 'bg-red-100 text-red-800',
  SUSPENDED: 'bg-gray-100 text-gray-800',
  NOT_PROVIDED: 'bg-gray-100 text-gray-600',
  REJECTED: 'bg-red-100 text-red-800',
}

const verificationBadgeClass: Record<string, string> = {
  UNVERIFIED: 'bg-gray-100 text-gray-800',
  PENDING_REVIEW: 'bg-blue-100 text-blue-800',
  VERIFIED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
}

export default function ComplianceDocumentDetailPage({ params }: { params?: { id?: string } }) {
  const documentId = params?.id ?? null
  const t = useT()
  const [row, setRow] = React.useState<DocumentRow | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const formSchema = React.useMemo(() => createComplianceDocumentFormSchema(), [])
  const fields = React.useMemo(
    () => createComplianceDocumentFormFields(t, { lockedParticipantId: null }),
    [t],
  )
  const groups = React.useMemo(() => createComplianceDocumentFormGroups(t), [t])

  const loadDetail = React.useCallback(async () => {
    if (!documentId) {
      setRow(null)
      setError(t('compliance.detail.errors.missingId', 'Compliance document id is missing.'))
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const payload = await readApiResultOrThrow<ListResult>(
        `/api/compliance/documents?ids=${encodeURIComponent(documentId)}&pageSize=1`,
        undefined,
        { errorMessage: t('compliance.detail.loadError', 'Failed to load compliance document.') },
      )
      const item = Array.isArray(payload?.items) && payload.items.length > 0 ? payload.items[0] : null
      if (!item) {
        setError(t('compliance.detail.notFound', 'Compliance document was not found.'))
        setRow(null)
      } else {
        setRow(item)
      }
    } catch (loadError) {
      const message = loadError instanceof Error && loadError.message
        ? loadError.message
        : t('compliance.detail.loadError', 'Failed to load compliance document.')
      setError(message)
      setRow(null)
    } finally {
      setIsLoading(false)
    }
  }, [documentId, t])

  React.useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  const contentHeader = React.useMemo(() => {
    if (!row) return null
    const status = typeof row.status === 'string' ? row.status : null
    const verificationStatus = typeof row.verification_status === 'string' ? row.verification_status : null
    const documentType = typeof row.document_type === 'string' ? row.document_type : null
    const documentNumber = typeof row.document_number === 'string' ? row.document_number : null
    return (
      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('compliance.detail.label', 'Compliance document')}
            </div>
            <div className="text-base font-semibold">
              {documentType ? t(`compliance.document_type.${documentType}`, documentType) : '-'}
            </div>
            {documentNumber ? (
              <div className="text-xs text-muted-foreground">{documentNumber}</div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {status ? (
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass[status] ?? 'bg-gray-100 text-gray-800'}`}>
                {t(`compliance.status.${status}`, status)}
              </span>
            ) : null}
            {verificationStatus ? (
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${verificationBadgeClass[verificationStatus] ?? 'bg-gray-100 text-gray-800'}`}>
                {t(`compliance.verification_status.${verificationStatus}`, verificationStatus)}
              </span>
            ) : null}
            {row.is_indefinite === true ? (
              <Badge variant="secondary">{t('compliance.fields.isIndefinite', 'Indefinite validity')}</Badge>
            ) : null}
          </div>
        </div>
      </div>
    )
  }, [row, t])

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('compliance.detail.loading', 'Loading compliance document…')} />
        </PageBody>
      </Page>
    )
  }

  if (error || !row || !documentId) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={error ?? t('compliance.detail.notFound', 'Compliance document was not found.')} />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageBody>
        <CrudForm<ComplianceDocumentFormValues>
          title={t('compliance.detail.title', 'Edit compliance document')}
          backHref="/backend/compliance-documents"
          cancelHref="/backend/compliance-documents"
          deleteRedirect="/backend/compliance-documents"
          submitLabel={t('compliance.form.submitUpdate', 'Save changes')}
          schema={formSchema}
          fields={fields}
          groups={groups}
          entityId={COMPLIANCE_DOCUMENT_ENTITY_ID}
          initialValues={mapComplianceDocumentRowToFormValues(row)}
          contentHeader={contentHeader}
          onSubmit={async (values) => {
            await updateCrud('compliance/documents', buildComplianceDocumentUpdatePayload(documentId, values))
            flash(t('compliance.actions.updated', 'Compliance document saved.'), 'success')
            await loadDetail()
          }}
          onDelete={async () => {
            await deleteCrud('compliance/documents', documentId)
          }}
        />
      </PageBody>
    </Page>
  )
}
